import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { useSelector } from "src/store";
import { selectCurrentWorkspaceId } from "src/store/app";
import { updateClientFeature } from "src/ui/containers/clients/api";
import { maxInt } from "src/ui/utils/limit";

import { Box } from "@mui/material";
import { PrimaryButton } from "@shared/CustomButton";
import { UncontrolledNumberField } from "@shared/form/NumberField";
import { SelectField } from "@shared/form/SelectField/SelectField";
import { Modal } from "@shared/Modal/Modal";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

type ChangeLimitsModalProps = {
    open: boolean;
    onClose: () => void;
    initialLimitType?: string;
    initialValue?: number;
    clientId?: number;
    onSuccess?: (limitType: string, newValue: number) => void;
};

const limitOptions = [
    { value: "BuildLimit", label: i18next.t("common:limits.BuildLimit") },
    { value: "FDroidRepoLimit", label: i18next.t("common:limits.FDroidRepoLimit") },
    { value: "FileSizeLimit", label: i18next.t("common:limits.FileSizeLimit") },
    { value: "FileCountLimit", label: i18next.t("common:limits.FileCountLimit") },
    { value: "GroupNestingLimit", label: i18next.t("common:limits.GroupNestingLimit") },
    { value: "LicenseLimit", label: i18next.t("common:limits.LicenseLimit") },
    { value: "ProvisioningLimit", label: i18next.t("common:limits.ProvisioningLimit") },
    { value: "ProductLimit", label: i18next.t("common:limits.ProductLimit") },
    { value: "UserLimit", label: i18next.t("common:limits.UserLimit") },
];

export const ChangeLimitsModal = (props: ChangeLimitsModalProps) => {
    const { t } = useTranslation(["modals", "common"]);
    const [limitType, setLimitType] = useState(props.initialLimitType || "");
    const [limitValue, setLimitValue] = useState(props.initialValue?.toString() || "");
    const [isLoading, setIsLoading] = useState(false);
    const [valueError, setValueError] = useState("");
    const currentWorkspaceId = useSelector(selectCurrentWorkspaceId);

    React.useEffect(() => {
        setLimitType(props.initialLimitType || "");
        setLimitValue(props.initialValue?.toString() || "");
    }, [props.initialLimitType, props.initialValue]);

    const handleClose = () => {
        setLimitType("");
        setLimitValue("");
        setValueError("");
        props.onClose();
    };

    const handleValueChange = (value: string) => {
        setLimitValue(value);
        const numValue = parseInt(value);
        if (numValue < 0) {
            setValueError(t("modals:changeLimits.input.limitValue.validation.negative"));
        } else if (numValue > maxInt) {
            setValueError(t("modals:changeLimits.input.limitValue.validation.tooLarge"));
        } else {
            setValueError("");
        }
    };

    const handleSave = async () => {
        if (!currentWorkspaceId || !props.clientId) {
            return;
        }

        if (valueError) {
            showErrorToast(valueError);
            return;
        }

        const valueToSave = parseInt(limitValue);

        setIsLoading(true);
        try {
            await updateClientFeature({
                workspaceId: currentWorkspaceId,
                clientId: props.clientId,
                type: limitType,
                value: valueToSave,
            });

            showSuccessToast(t("modals:changeLimits.toast.limitUpdatedSuccess"));

            props.onSuccess?.(limitType, valueToSave);

            handleClose();
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("modals:changeLimits.toast.limitUpdateFailed"));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal
            open={props.open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            title={t("modals:changeLimits.title")}
            endButton={
                <PrimaryButton
                    onClick={handleSave}
                    disabled={!limitType || !limitValue || isLoading || !!valueError}
                    loading={isLoading}
                >
                    {t("common:button.save")}
                </PrimaryButton>
            }
        >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <SelectField
                    label={t("modals:changeLimits.input.limit.caption")}
                    value={limitOptions.find((option) => option.value === limitType)}
                    onChange={(_event, selectedOption) => {
                        if (!selectedOption || typeof selectedOption.value !== "string") {
                            return;
                        }
                        setLimitType(selectedOption.value);
                    }}
                    fullWidth
                    options={limitOptions}
                />
                <UncontrolledNumberField
                    label={t("modals:changeLimits.input.limitValue.caption")}
                    value={limitValue}
                    onChange={(e) => handleValueChange(e.target.value)}
                    fullWidth
                    placeholder={t("modals:changeLimits.input.limitValue.placeholder")}
                    errorMessage={valueError}
                    slotProps={{ htmlInput: { min: 0, max: maxInt } }}
                />
            </Box>
        </Modal>
    );
};
